import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardPageFormBody,
  buildDashboardDataTableColumns,
  useDashboardDataTableClientPage,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { DashboardSelectionDialog } from '../../../../../components/DashboardSelectionDialog';
import { createTargetingSummaryColumns } from '../../../../targeting/builder/components/TargetingSummaryColumns';
import { useTargetingBuilderDeviceTypesQuery } from '../../../../targeting/builder/hooks';
import { manageDialogFetchSize } from '../../../../../lib/clientSelectionList';
import { useDealTargetingsQuery } from '../../hooks';
import { type DealFormValue, type DealLookupFilter, type DealTargeting } from '../../model';

const lookupFilter: DealLookupFilter = {
  searchTerm: '',
  page: 0,
  size: manageDialogFetchSize,
};

export function DealTargetingFields() {
  'use no memo';
  const { control, setValue } = useFormContext<DealFormValue>();
  const selected = useWatch({ control, name: 'targetings' });
  const [manageOpen, setManageOpen] = React.useState(false);
  const query = useDealTargetingsQuery(lookupFilter, manageOpen);
  const deviceTypes = useTargetingBuilderDeviceTypesQuery();
  const names = new Map((deviceTypes.data ?? []).map((item) => [item.id, item.name]));
  const summaryColumns = createTargetingSummaryColumns<DealTargeting>(names);
  const columns = buildDashboardDataTableColumns<DealTargeting>([
    {
      id: 'name',
      header: 'Name',
      kind: DashboardDataTableColumnKind.Text,
      value: (item) => item.name,
      headerClassName: 'min-w-[280px]',
      className: 'min-w-[280px]',
    },
    ...summaryColumns,
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      header: 'Actions',
      cell: (item) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove ${item.name}`}
          onClick={() => {
            setValue(
              'targetings',
              selected.filter((candidate) => candidate.id !== item.id),
              { shouldDirty: true },
            );
          }}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      ),
    },
  ]);

  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(selected);
  return (
    <DashboardPageFormBody width="lg">
      <DashboardDataTable
        title="Targeting"
        tableLabel="Selected targeting"
        columns={columns}
        items={tablePage.items}
        pagination={tablePage.pagination}
        getRowKey={(item) => item.id}
        emptyMessage="No targeting selected."
        minWidthClassName="min-w-[680px]"
        toolbar={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setManageOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Manage targeting
            </Button>
            {selected.length ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setValue('targetings', [], { shouldDirty: true });
                }}
              >
                Remove all
              </Button>
            ) : null}
          </>
        }
      />
      <DashboardSelectionDialog
        open={manageOpen}
        title="Manage targeting"
        description="Select targeting definitions for this deal."
        items={query.data?.content ?? []}
        selected={selected}
        loading={query.isLoading || query.isFetching}
        error={query.isError}
        getId={(item) => item.id}
        getName={(item) => item.name}
        columns={summaryColumns}
        searchLabel="Search targeting"
        loadingMessage="Loading targeting…"
        errorMessage="Targeting could not be loaded."
        emptyMessage="No targeting found."
        onOpenChange={setManageOpen}
        onConfirm={(items) => {
          setValue('targetings', items, { shouldDirty: true });
        }}
      />
    </DashboardPageFormBody>
  );
}
